import { useState, useMemo } from 'react';
import { Typography, Grid } from '@mui/material';
import { useQueryClient } from 'react-query';

// Our components
import Alert from 'components/Alert';
import CurrencyTextInput from 'components/Input/CurrencyTextInput';
import Input from 'components/Input/TextInput';
import Loader from 'components/Loader';
import LoanInquirySuccessModal from 'components/Modal/LoanInquiryConfirmationModal';
import SoraTextField from 'components/Input/SoraTextField';
import USStateDropdown from 'components/Dropdown/USStateDropdown';
import { PrimaryButton } from 'components/Button/Button';
import RequiredFieldsTooltip from 'components/Forms/NewLoanForms/RequiredFieldsTooltip';
import { NUMBER, TEXT } from 'components/Input/Types';

// Our hooks 🪝
import useMutateCreateDeal from 'hooks/hubspot/useMutateCreateDeal';
import useMutateSaveNewLoanInquiry from 'hooks/newLoanInquiries/useMutateSaveNewLoanInquiry';
import useMutateUpdateLoanInquiry from 'hooks/newLoanInquiries/useMutateUpdatedLoanInquiry';
import useMutateSendGenericEmail from 'hooks/emails/useMutateSendGenericEmail';

// Our Query Keys
import { ERROR_MESSAGE_DATA } from 'shared/query-keys';

// Utils
import { clearFormValues, isSubmissionReady } from 'shared/utils';
import createDefaultCloseDate from 'shared/utils/hubspot/createDefaultCloseDate';
import formatCollection from 'shared/utils/formatting/currency/formatCollection';

// Our emails
import lendingEmailTemplate from 'emails/lendingEmailTemplate';

// Constants
import { LOT, NEW_LOAN_OFFER_TYPE } from 'shared/constants';

function LotLoanForm({
	advisorInfo,
	clientInfo,
	isClientNameProvided,
	loanType,
	loanRequestId,
	financialData,
	postSuccessEvent
}) {
	const queryClient = useQueryClient();
	const updateLoanInquiry = useMutateUpdateLoanInquiry();

	// API Calls
	const saveLoanInquiry = useMutateSaveNewLoanInquiry();
	const { isLoading: savingLoanInquiry } = saveLoanInquiry;

	// Mutations
	const sendGenericEmail = useMutateSendGenericEmail();
	const createDeal = useMutateCreateDeal();

	// Build full name
	const {
		clientFullName,
		clientId,
		firstName,
		lastName,
		creditScoreValue,
		emailAddressValue
	} = clientInfo;

	const { advisorName, advisorEmail, advisorCompany, advisorWebsite } =
		advisorInfo;

	const [loanAmount, setLoanAmount] = useState('');
	const [state, setState] = useState('');
	const [county, setCounty] = useState('');
	const [estimatedPurchaseValue, setEstimatedPurchaseValue] = useState('');
	const [downPaymentPercent, setDownPaymentPercent] = useState('');
	const [acres, setAcres] = useState('');
	const [landUse, setLandUse] = useState('');
	const [timeUntilConstruction, setTimeUntilConstruction] = useState('');
	const [desiredLoanTerm, setDesiredLoanTerm] = useState('');
	const [totalIncomeOfApplicants, setTotalIncomeOfApplicants] = useState('');

	const [additionalNotes, setAdditionalNotes] = useState('');
	const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

	const requiredFormValues = useMemo(
		() => [loanAmount, desiredLoanTerm, county, state, downPaymentPercent],
		[loanAmount, desiredLoanTerm, county, state, downPaymentPercent]
	);

	const isFormReady = useMemo(
		() => isClientNameProvided && isSubmissionReady(requiredFormValues),
		[isClientNameProvided, requiredFormValues]
	);

	const sendFormData = () => {
		const sendToArray = ['lending@sorafinance.com'];
		const subject = `Lot Loan Request - ${firstName} ${lastName}`;

		const emailTemplate = lendingEmailTemplate({
			advisorInfo,
			clientInfo,
			financialData,
			clientFullName,
			loanAmount,
			loanRelatedDetails: [
				`Desired loan term: ${desiredLoanTerm}`,
				`Total income of applicants: ${totalIncomeOfApplicants}`,
				`County client is applying in: ${county}`,
				`State client is applying in: ${state}`,
				`Estimated purchase value: ${estimatedPurchaseValue}`,
				`Down payment percent: ${downPaymentPercent}`,
				`# of acres: ${acres}`,
				`Eventual land use: ${landUse}`,
				`Time until construction starts: ${timeUntilConstruction}`
			],
			additionalNotes,
			lendingTypeRequested: 'Lot Loan'
		});

		sendGenericEmail.mutate(
			{
				firstName,
				lastName,
				subject,
				message: emailTemplate,
				sendToArray
			},
			{
				onSuccess: () => {
					clearFormValues([
						setLoanAmount,
						setEstimatedPurchaseValue,
						setState,
						setCounty,
						setDownPaymentPercent,
						setAcres,
						setLandUse,
						setTimeUntilConstruction,
						setDesiredLoanTerm,
						setAdditionalNotes
					]);
				}
			}
		);
	};

	const submitNewLoanInquiry = () => {
		const newLoanInquiryData = {
			acres,
			additionalNotes,
			clientId,
			county,
			creditScore: creditScoreValue,
			desiredLoanTerm,
			downPaymentPercent,
			emailAddress: emailAddressValue,
			loanAmount,
			estimatedPurchaseValue,
			firstName,
			landUse,
			lastName,
			offerType: NEW_LOAN_OFFER_TYPE,
			state,
			timeUntilConstruction,
			totalAnnualIncome: totalIncomeOfApplicants,
			tradelineType: LOT
		};

		// Call mutation to update user's data
		saveLoanInquiry.mutate(newLoanInquiryData, {
			onSuccess: () => {
				setIsSuccessModalOpen(true);
			},
			onError: () => {
				// Set error message
				queryClient.setQueryData(
					ERROR_MESSAGE_DATA,
					'There was an error making your loan request.'
				);
			}
		});
	};

	const updateLoanRequest = () => {
		const updatedLoanInquiryData = {
			acres,
			additionalNotes,
			clientId,
			county,
			creditScore: creditScoreValue,
			desiredLoanTerm,
			downPaymentPercent,
			emailAddress: emailAddressValue,
			estimatedPurchaseValue,
			firstName,
			landUse,
			lastName,
			loanAmount,
			offerType: NEW_LOAN_OFFER_TYPE,
			state,
			timeUntilConstruction,
			totalAnnualIncome: totalIncomeOfApplicants,
			tradelineType: LOT
		};

		// Call mutation to update user's data
		updateLoanInquiry.mutate(
			{ loanRequestId, updatedLoanInquiryData },
			{
				onSuccess: () => {
					clearFormValues([
						setLoanAmount,
						setEstimatedPurchaseValue,
						setState,
						setCounty,
						setDownPaymentPercent,
						setAcres,
						setLandUse,
						setTimeUntilConstruction,
						setDesiredLoanTerm,
						setAdditionalNotes
					]);

					if (postSuccessEvent) {
						postSuccessEvent();
					}
				},
				onError: () => {
					// Set error message
					queryClient.setQueryData(
						ERROR_MESSAGE_DATA,
						'There was an error making your loan request.'
					);
				}
			}
		);
	};

	const { isLoading } = sendFormData;

	const currentURL = window.location.href;
	const atLoanRequestDetailsUrl = currentURL.includes('loan-request-details');

	const submitForm = (event) => {
		event.preventDefault();

		const closeDate = createDefaultCloseDate();
		const valuesToFormat = [
			{
				type: 'currency',
				value: totalIncomeOfApplicants,
				label: 'Annual Income:'
			},
			{
				type: 'currency',
				value: loanAmount,
				label: 'Desired loan amount:'
			},
			{
				type: 'currency',
				value: estimatedPurchaseValue,
				label: 'Estimated Purchase Value:'
			},
			{
				type: 'percent',
				value: downPaymentPercent,
				label: 'Down payment percent:'
			}
		];

		const formattedCollection = formatCollection(valuesToFormat);
		const advisorNotes = `Advisor's name: ${advisorName}
				Advisor's email: ${advisorEmail}
				Advisor's company: ${advisorCompany}
				Credit Score: ${creditScoreValue}
				${formattedCollection}
				Desired loan Term in years: ${desiredLoanTerm}
				County you are buying in: ${county}
				State: ${state}
				Number of acres: ${acres}
				Eventual use of land: ${landUse}
				Time until construction starts in months: ${timeUntilConstruction}
				------------------------------------------------------------------------------------------------
				Advisor's notes: \n${additionalNotes}`;

		createDeal.mutate({
			advisorInfo: {
				advisorName,
				email: advisorEmail,
				companyName: advisorCompany,
				companyDomain: advisorWebsite
			},
			properties: {
				amount: loanAmount,
				dealname: `${advisorName} / ${clientFullName}`,
				dealtype: loanType,
				borrower: clientFullName,
				closedate: closeDate
			},
			noteInfo: {
				hs_note_body: advisorNotes
			}
		});

		// If we're at /loan-inquiry, perform the following
		if (!atLoanRequestDetailsUrl) {
			submitNewLoanInquiry();
			sendFormData();
		}
		// Else if we're at /loan-inquiry-details, perform the following
		else if (atLoanRequestDetailsUrl) {
			// Call new mutations here patching the new loan inquiry
			updateLoanRequest();
		}
	};

	// Handlers for mutation
	const { isError: sendFormDataError } = sendFormData;

	return (
		<>
			{!atLoanRequestDetailsUrl && (
				<Grid item xs={12}>
					<Typography variant="h2Gascogne">Lot Loan</Typography>

					<Typography variant="body2">
						Fill in the information below to submit a loan request
						to Sora. You will be able to complete and review your
						loan request in the dashboard task bar.
					</Typography>
				</Grid>
			)}

			{isLoading || (savingLoanInquiry && <Loader />)}
			{!isLoading && !savingLoanInquiry && (
				<>
					{sendFormDataError && <Alert variant="error" />}
					{/* Modal shown on successful submission of  */}
					<LoanInquirySuccessModal isOpen={isSuccessModalOpen} />

					<Grid item xs={12} md={6}>
						{/* Desired Loan Amount */}
						<CurrencyTextInput
							label="Desired loan amount"
							value={loanAmount}
							onChange={setLoanAmount}
							required
							error={loanAmount === ''}
							helperText="Desired loan amount cannot be empty"
							type={NUMBER}
							inputProps={{
								'data-test': 'loanAmount'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Desired loan term */}
						<Input
							label="Desired loan term (# of years)"
							value={desiredLoanTerm}
							onChange={setDesiredLoanTerm}
							required
							error={desiredLoanTerm === ''}
							helperText="Desired loan term cannot be empty"
							type={NUMBER}
							inputProps={{
								'data-test': 'desiredLoanTerm'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Annual Income */}
						<CurrencyTextInput
							label="Total annual income (of everyone applying)"
							value={totalIncomeOfApplicants}
							onChange={setTotalIncomeOfApplicants}
							inputProps={{
								'data-test': 'annualIncome'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* County / State */}
						<Input
							label="County you are buying in"
							value={county}
							onChange={setCounty}
							required
							error={county === ''}
							helperText="County cannot be empty"
							type={TEXT}
							inputProps={{
								'data-test': 'countyState'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* State */}
						<USStateDropdown
							state={state}
							setState={setState}
							inputProps={{
								'data-test': 'state'
							}}
							gridColumns={12}
							required
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Estimated Home Value */}
						<CurrencyTextInput
							label="Estimated Purchase Value"
							subLabel="Estimated price of property"
							value={estimatedPurchaseValue}
							onChange={setEstimatedPurchaseValue}
							inputProps={{
								'data-test': 'estimatedPurchaseValue'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Down payment % */}
						<Input
							type="percent"
							label="Down payment %"
							onChange={setDownPaymentPercent}
							value={downPaymentPercent}
							required
							error={downPaymentPercent === ''}
							helperText="Down payment cannot be empty"
							inputProps={{
								'data-test': 'downPaymentPercent'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Acres */}
						<Input
							label="Number of acres"
							subLabel="Approximate number of acres of property"
							value={acres}
							onChange={setAcres}
							type={NUMBER}
							inputProps={{
								'data-test': 'numAcres'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Land use input */}
						<Input
							label="Eventual use of land"
							subLabel="(e.g. secondary residence, investment property)"
							value={landUse}
							onChange={setLandUse}
							type={TEXT}
							inputProps={{
								'data-test': 'landUse'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Time until construction starts */}
						<Input
							label="Time until construction starts (months)"
							value={timeUntilConstruction}
							onChange={setTimeUntilConstruction}
							type={NUMBER}
							inputProps={{
								'data-test': 'timeUntilConstruction'
							}}
						/>
					</Grid>

					<Grid item xs={12}>
						<SoraTextField
							label="Additional Notes"
							value={additionalNotes}
							onChange={setAdditionalNotes}
						/>
					</Grid>
				</>
			)}

			<Grid item xs={12}>
				{!isFormReady && (
					<RequiredFieldsTooltip>
						<span>
							<PrimaryButton
								disabled={!isFormReady}
								onClick={submitForm}
							>
								Submit
							</PrimaryButton>
						</span>
					</RequiredFieldsTooltip>
				)}
				{isFormReady && (
					<PrimaryButton onClick={submitForm}>Submit</PrimaryButton>
				)}
			</Grid>
		</>
	);
}

export default LotLoanForm;
